/**
 * Horaires propres à une salle (zone_time_windows) — source unique.
 * Les écrans (src/lib/zoneScheduleUtils.ts) réexportent ce fichier : la parité
 * écrans/serveur est garantie par construction (même code).
 *
 * Règle : une salle ACTIVE qui possède au moins une plage active REMPLACE les
 * services du restaurant (y compris les dates exceptionnelles). Sinon, on renvoie
 * `null` et l'appelant garde exactement le calcul historique.
 * Jours vides = jamais (aligné sur la base, qui interdit une plage sans jour).
 */
export interface ZoneWindow {
  zoneId: string;
  startTime: string;
  endTime: string;
  availableDays: number[]; // 0 = dimanche ; vide = jamais
  isActive: boolean;
}

export interface ZoneSlot {
  start: string;
  end: string;
  availableDays?: number[];
  durationOverrideMinutes?: number | null;
}

/** activeZoneIds : si fourni, une salle absente de l'ensemble (désactivée) suit le restaurant. */
export function zoneHasOwnSchedule(
  zoneId: string | null | undefined,
  windows: ZoneWindow[],
  activeZoneIds?: Set<string>,
): boolean {
  if (!zoneId) return false;
  if (activeZoneIds && !activeZoneIds.has(zoneId)) return false;
  return windows.some(w => w.isActive && w.zoneId === zoneId);
}

/** Plages de la salle pour un jour de semaine, ou `null` si la salle suit le restaurant. */
export function getZoneSlotsForDay(
  dayOfWeek: number,
  zoneId: string | null | undefined,
  windows: ZoneWindow[],
  activeZoneIds?: Set<string>,
): ZoneSlot[] | null {
  if (!zoneHasOwnSchedule(zoneId, windows, activeZoneIds)) return null;
  return windows
    .filter(w => w.isActive && w.zoneId === zoneId)
    .filter(w => Array.isArray(w.availableDays) && w.availableDays.includes(dayOfWeek))
    .map(w => ({ start: w.startTime.substring(0, 5), end: w.endTime.substring(0, 5), durationOverrideMinutes: null }))
    .sort((a, b) => a.start.localeCompare(b.start));
}

export function mapZoneWindowRow(row: {
  zone_id: string; start_time: string; end_time: string; available_days: number[] | null; is_active: boolean;
}): ZoneWindow {
  return {
    zoneId: row.zone_id,
    startTime: row.start_time,
    endTime: row.end_time,
    availableDays: row.available_days ?? [],
    isActive: row.is_active,
  };
}

/** Pseudo-créneau au format des lignes time_slots (id null : aucune limite ni fermeture de service). */
export interface ZonePseudoSlotRow {
  id: null;
  start_time: string;
  end_time: string;
  available_days: number[];
  duration_override_minutes: null;
  is_active: true;
}

/**
 * Créneaux d'une demande client selon les salles choisies.
 * Renvoie `null` (calcul historique inchangé) sauf si TOUTES les salles choisies
 * sont actives et ont des horaires propres ; dans ce cas, l'union de leurs plages du jour.
 */
export function resolveZoneSlotRows(
  dayOfWeek: number,
  zonePreference: unknown,
  windows: ZoneWindow[],
  activeZoneIds: Set<string>,
): ZonePseudoSlotRow[] | null {
  if (!Array.isArray(zonePreference) || zonePreference.length === 0) return null;
  const zones = zonePreference.filter((z): z is string => typeof z === 'string' && z.length > 0);
  if (zones.length === 0 || zones.length !== zonePreference.length) return null;
  if (!zones.every(z => zoneHasOwnSchedule(z, windows, activeZoneIds))) return null;
  const seen = new Set<string>();
  const rows: ZonePseudoSlotRow[] = [];
  for (const z of zones) {
    for (const s of getZoneSlotsForDay(dayOfWeek, z, windows, activeZoneIds) ?? []) {
      const key = `${s.start}-${s.end}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({ id: null, start_time: `${s.start}:00`, end_time: `${s.end}:00`, available_days: [], duration_override_minutes: null, is_active: true });
    }
  }
  return rows.sort((a, b) => a.start_time.localeCompare(b.start_time));
}

/** Charge les plages et salles actives puis résout. Aucune requête si aucune salle choisie. */
// deno-lint-ignore no-explicit-any
export async function loadZoneSlotRows(supabase: any, establishmentId: string, dayOfWeek: number, zonePreference: unknown): Promise<ZonePseudoSlotRow[] | null> {
  if (!Array.isArray(zonePreference) || zonePreference.length === 0) return null;
  const [{ data: wins, error: wErr }, { data: zones, error: zErr }] = await Promise.all([
    supabase.from('zone_time_windows').select('zone_id, start_time, end_time, available_days, is_active').eq('establishment_id', establishmentId).eq('is_active', true),
    supabase.from('bookable_zones').select('id').eq('establishment_id', establishmentId).eq('is_active', true),
  ]);
  if (wErr || zErr) throw (wErr || zErr);
  if (!wins || wins.length === 0) return null;
  return resolveZoneSlotRows(dayOfWeek, zonePreference, wins.map(mapZoneWindowRow), new Set((zones || []).map((z: { id: string }) => z.id)));
}
